export const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/** Below this a single PUT is simpler and finishes in one round trip. */
const MULTIPART_THRESHOLD = 200 * 1024 * 1024;
/** Parts in flight at once — enough to saturate a home uplink, not enough to starve it. */
const PART_CONCURRENCY = 3;
/** A part is cheap to retry, so give a flaky connection a few chances. */
const PART_ATTEMPTS = 4;

class ApiService {
  async request(endpoint, options = {}) {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options, headers, credentials: 'include',
    });
    if (response.status === 401) {
      window.location.href = '/auth/sign-in';
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.error || error.message || `API Error: ${response.statusText}`);
    }
    if (response.status === 204) return null;
    return response.json();
  }

  // ── Analytics ──────────────────────────────────────────────────────────────
  getAnalyticsOverview() { return this.request('/api/admin/analytics/overview'); }

  // ── Titles ────────────────────────────────────────────────────────────────
  getTitles(type, filters = {}) {
    const params = new URLSearchParams({ ...(type && { type }), ...filters });
    return this.request(`/api/admin/titles?${params}`);
  }
  getTitle(id)          { return this.request(`/api/admin/titles/${id}`); }
  createTitle(data)     { return this.request('/api/admin/titles',       { method: 'POST',   body: JSON.stringify(data) }); }
  updateTitle(id, data) { return this.request(`/api/admin/titles/${id}`, { method: 'PUT',    body: JSON.stringify(data) }); }
  deleteTitle(id)       { return this.request(`/api/admin/titles/${id}`, { method: 'DELETE' }); }

  // ── Upload ────────────────────────────────────────────────────────────────
  getUploadUrl(filename, contentType, kind, size) {
    return this.request('/api/admin/upload-url', {
      method: 'POST',
      body: JSON.stringify({ filename, contentType, kind, size }),
    });
  }
  /**
   * Uploads to R2 and returns the stored key.
   *
   * Small files go up in one presigned PUT. Large video goes up in parts: a
   * single PUT cannot exceed 5 GiB and cannot be resumed, so one dropped
   * connection used to cost the whole film. Parts are sent a few at a time and
   * retried individually, and `onProgress(ratio)` reports how far along it is.
   */
  async uploadFile(file, kind, onProgress) {
    if (kind === 'video' && file.size > MULTIPART_THRESHOLD) {
      return this.uploadFileMultipart(file, onProgress);
    }

    const { uploadUrl, key, contentType } = await this.getUploadUrl(file.name, file.type || 'application/octet-stream', kind, file.size);
    const res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' }, body: file });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        /expired|AccessDenied/i.test(detail)
          ? 'The upload link timed out before the file finished sending. Check the connection and try again.'
          : `Upload failed (${res.status})`,
      );
    }
    onProgress?.(1);
    return key;
  }

  multipart(body) {
    return this.request('/api/admin/upload-multipart', { method: 'POST', body: JSON.stringify(body) });
  }

  async uploadFileMultipart(file, onProgress) {
    const { key, uploadId, partSize } = await this.multipart({
      action: 'create',
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    });

    const partCount = Math.ceil(file.size / partSize);
    let uploaded = 0;

    try {
      // URLs are signed a hundred at a time: one request per batch rather than
      // one per part, and not thousands of them up front either.
      for (let start = 1; start <= partCount; start += 100) {
        const partNumbers = [];
        for (let part = start; part < start + 100 && part <= partCount; part += 1) partNumbers.push(part);
        const { urls } = await this.multipart({ action: 'sign', key, uploadId, partNumbers });

        // A shared cursor hands the next free worker the next part, so one slow
        // part does not hold up the rest.
        let next = 0;
        const sendOne = async () => {
          while (next < urls.length) {
            const { partNumber, url } = urls[next++];
            const from = (partNumber - 1) * partSize;
            const blob = file.slice(from, Math.min(from + partSize, file.size));

            let lastError;
            for (let attempt = 1; attempt <= PART_ATTEMPTS; attempt += 1) {
              try {
                const res = await fetch(url, { method: 'PUT', body: blob });
                if (!res.ok) throw new Error(`Part ${partNumber} failed (${res.status})`);
                uploaded += blob.size;
                onProgress?.(uploaded / file.size);
                lastError = undefined;
                break;
              } catch (error) {
                lastError = error;
                await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
              }
            }
            if (lastError) throw lastError;
          }
        };

        await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, urls.length) }, sendOne));
      }

      await this.multipart({ action: 'complete', key, uploadId });
      return key;
    } catch (error) {
      // Orphaned parts are billed forever, so clear them even on failure.
      this.multipart({ action: 'abort', key, uploadId }).catch(() => {});
      throw error;
    }
  }

  // ── Seasons & Episodes ────────────────────────────────────────────────────
  createSeason(titleId, seasonNumber) {
    return this.request(`/api/admin/titles/${titleId}/seasons`, {
      method: 'POST', body: JSON.stringify({ seasonNumber }),
    });
  }
  deleteSeason(seasonId) {
    return this.request(`/api/admin/seasons/${seasonId}`, { method: 'DELETE' });
  }
  createEpisode(seasonId, data) {
    return this.request(`/api/admin/seasons/${seasonId}/episodes`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }
  deleteEpisode(episodeId) {
    return this.request(`/api/admin/episodes/${episodeId}`, { method: 'DELETE' });
  }

  // ── Notifications ─────────────────────────────────────────────────────────
  getNotifications() { return this.request('/api/admin/notifications'); }

  // ── Users ─────────────────────────────────────────────────────────────────
  getUsers()               { return this.request('/api/admin/users'); }
  updateUserRole(id, role) { return this.request(`/api/admin/users/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) }); }
  deleteUser(id)           { return this.request(`/api/admin/users/${id}`, { method: 'DELETE' }); }

  // ── Genres ────────────────────────────────────────────────────────────────
  getGenres()            { return this.request('/api/admin/genres'); }
  createGenre(name)      { return this.request('/api/admin/genres',        { method: 'POST', body: JSON.stringify({ name }) }); }
  updateGenre(id, name)  { return this.request(`/api/admin/genres/${id}`,  { method: 'PUT',  body: JSON.stringify({ name }) }); }
  deleteGenre(id)        { return this.request(`/api/admin/genres/${id}`,  { method: 'DELETE' }); }

  // ── VJs ───────────────────────────────────────────────────────────────────
  getVjs()           { return this.request('/api/admin/vjs'); }
  createVj(name)     { return this.request('/api/admin/vjs',       { method: 'POST', body: JSON.stringify({ name }) }); }
  updateVj(id, name) { return this.request(`/api/admin/vjs/${id}`, { method: 'PUT',  body: JSON.stringify({ name }) }); }
  deleteVj(id)       { return this.request(`/api/admin/vjs/${id}`, { method: 'DELETE' }); }

  // ── Storage ───────────────────────────────────────────────────────────────
  getStorage() { return this.request('/api/admin/storage'); }

  // ── Auth ──────────────────────────────────────────────────────────────────
  getSession() {
    return this.request('/api/auth/session');
  }

  updateProfile(data) {
    return this.request('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  login(email, password) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout() {
    try { await this.request('/api/auth/logout', { method: 'POST' }); } finally {
      window.location.href = '/auth/sign-in';
    }
  }

  // ── Generate description (AI) ─────────────────────────────────────────────
  generateDescription(title, year) {
    return this.request('/api/admin/generate-description', {
      method: 'POST', body: JSON.stringify({ title, year }),
    });
  }

  // ── Users, VJs, Genres ──────────────────────────────────────────────────
  async getUsers() {
    return this.request("/api/admin/users");
  }

  async getVjs() {
    return this.request("/api/admin/vjs");
  }

  async createVj(data) {
    return this.request("/api/admin/vjs", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateVj(id, data) {
    return this.request(`/api/admin/vjs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteVj(id) {
    return this.request(`/api/admin/vjs/${id}`, {
      method: "DELETE",
    });
  }

  async getGenres() {
    return this.request("/api/admin/genres");
  }

  async createGenre(data) {
    return this.request("/api/admin/genres", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateGenre(id, data) {
    return this.request(`/api/admin/genres/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteGenre(id) {
    return this.request(`/api/admin/genres/${id}`, {
      method: "DELETE",
    });
  }
}

const apiService = new ApiService();
export default apiService;
